import React from "react";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { FirebaseContext } from "@contexts";
import { Timestamp } from "firebase/firestore";

import { JsonForm } from "@components";
import { useStore } from "@contexts";
import { useAuth } from "@hooks";
import { getFormControl } from "./forms";
import { transformer } from "./transformer";
import { JsonFormControl } from "../JsonForm/types";
import { getPartTypeFromSerialNumber } from "@constants";

interface IFormRenderer {
  submit: (data: WithFieldValue<DocumentData>) => Promise<void>;
  stepName: string;
  disabled?: boolean;
  initialModuleId?: string | null;
}

function makeFilePath(fileExtension: string) {
  return `images/${crypto.randomUUID()}.${fileExtension}`;
}

function getImageExtension(file: File) {
  const { type } = file;
  if (type.startsWith("image/")) return type.split("/")[1];
  throw new Error(`file needs to be an image: invalid type ${type}`);
}

/**
 * Wrapper component to render the right JsonFormControl based on the  step
 * being performed.
 * @param submit Callback to handle form submission (db entry).
 * @param {string} stepName Name of the step being performed.
 * @param {boolean|undefined} disabled disables form elements if set (default false).
 */
function FormRenderer({
  submit,
  stepName,
  disabled = false,
  initialModuleId,
}: IFormRenderer) {
  const { moduleStatus } = useStore();
  const { user } = useAuth();
  const { storage } = React.useContext(FirebaseContext);

  const formControl = React.useMemo(
    () =>
      getFormControl(
        stepName,
        moduleStatus,
        user?.role === "admin" ? "Brown" : user?.location,
      ),
    [stepName],
  );

  // This is the key change: a memoized function to modify the form controls
  const newFormControl = React.useMemo(() => {
    // Make a deep copy to avoid mutating the original form
    if (!formControl) return formControl;
    const updatedFormControl = JSON.parse(JSON.stringify(formControl));

    // If no ID was passed, just return the form as-is
    if (!initialModuleId) {
      return updatedFormControl;
    }

    let targetFieldName: string | null = null;

    try {
      // 1. Identify what type of part the ID is for
      const partType = getPartTypeFromSerialNumber(initialModuleId);

      // 2. Map the part type to the correct form fieldName
      switch (partType.shorthand) {
        case "Sensor":
          targetFieldName = "sensorSerial";
          break;

        // Handle SEH, 2SSEH, PSSEH, etc.
        case "SEH":
        case "2S SEH":
        case "PSSEH":
          targetFieldName = "sehSerial";
          break;

        // Handle FEH, 2SFEH, PSFEH, etc.
        case "FEH":
        case "2SFEH":
        case "PSFEH":
          // Handle the "Left" vs "Right" case
          // We check the ID string for "L"
          if (
            initialModuleId.includes("L-") ||
            initialModuleId.includes("18L-")
          ) {
            targetFieldName = "leftFEHSerial";
          } else if (
            initialModuleId.includes("R-") ||
            initialModuleId.includes("18R-")
          ) {
            targetFieldName = "rightFEHSerial";
          } else {
            targetFieldName = "fehSerial"; // Fallback for a generic FEH
          }
          break;

        case "Sandwich":
          targetFieldName = "sandwichSerial";
          break;
        case "ROH":
          targetFieldName = "rohSerial";
          break;
        case "VTRX":
          targetFieldName = "vtrxSerial";
          break;
        default:
          targetFieldName = "moduleId"; // Fallback for other types
      }
    } catch (e) {
      // If getPartTypeFromSerialNumber fails, it's not a standard part.
      // Assume it's a module.
      targetFieldName = "moduleId";
    }

    // 3. Find the *specific* field that matches our target
    const targetField = updatedFormControl.find(
      (field: JsonFormControl) => field.fieldName === targetFieldName,
    );

    if (targetField) {
      // 4. Found the right field! Pre-fill it and keep it editable.
      targetField.defaultValue = initialModuleId;
      targetField.type = "string";
    } else {
      // **Fallback:** If we didn't find the specific field
      const fallbackField = updatedFormControl.find(
        (field: JsonFormControl) =>
          field.fieldName === "moduleId" ||
          field.fieldName === "moduleSerial" ||
          field.fieldName === "sensorSerial" ||
          field.fieldName === "sandwichSerial" ||
          field.fieldName === "fehSerial" ||
          field.fieldName === "sehSerial" ||
          field.fieldName === "rohSerial" ||
          field.fieldName === "vtrxSerial",
      );

      if (fallbackField) {
        fallbackField.defaultValue = initialModuleId;
        fallbackField.type = "string";
      }
    }

    return updatedFormControl;
  }, [formControl, initialModuleId]);

  const submitWithTransformer = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      if (data.image) {
        const filePath = makeFilePath(getImageExtension(data.image));
        const imageRef = ref(storage, filePath);
        let serial = "";
        if (data.sensorSerial) serial = data.sensorSerial;
        else if (data.moduleSerial) serial = data.moduleSerial;
        else if (data.sandwichSerial) serial = data.sandwichSerial;
        else if (data.fehSerial) serial = data.fehSerial;
        else if (data.sehSerial) serial = data.sehSerial;
        else if (data.rohSerial) serial = data.rohSerial;
        else if (data.vtrxSerial) serial = data.vtrxSerial;
        // This uploads the file to the storage bucket, and a cloud
        // function adds the image to the appropriate module.
        await uploadBytes(imageRef, data.image, {
          customMetadata: {
            metadata: JSON.stringify({
              path: filePath,
              module: serial,
              title: `${stepName} image`,
              comments: `Image from ` + stepName,
              userId: user?.firebaseUser?.uid || "",
              timestamp: Timestamp.now(),
              userName:
                user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : "",
              ...data.metadata,
            }),
          },
        });
      }
      if (data.multipleimage) {
        for await (const image of data.multipleimage) {
          const filePath = makeFilePath(getImageExtension(image));
          const imageRef = ref(storage, filePath);
          let serial = "";
          if (data.sensorSerial) serial = data.sensorSerial;
          else if (data.moduleSerial) serial = data.moduleSerial;
          else if (data.sandwichSerial) serial = data.sandwichSerial;
          else if (data.fehSerial) serial = data.fehSerial;
          else if (data.sehSerial) serial = data.sehSerial;
          else if (data.rohSerial) serial = data.rohSerial;
          else if (data.vtrxSerial) serial = data.vtrxSerial;
          const { metadata } = await uploadBytes(imageRef, image, {
            customMetadata: {
              metadata: JSON.stringify({
                path: filePath,
                module: serial,
                title: `${stepName} image`,
                comments: `Image from ` + stepName,
                userId: user?.firebaseUser?.uid || "",
                timestamp: Timestamp.now(),
                userName:
                  user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : "",
                ...data.metadata,
              }),
            },
          });
          const { fullPath } = metadata;
          if (!fullPath) {
            alert(
              `error: There was some error while uploading the file ${filePath}.`,
            );
          }
        }
      }
      if (data.multipleimage2) {
        for await (const image of data.multipleimage2) {
          const filePath = makeFilePath(getImageExtension(image));
          const imageRef = ref(storage, filePath);
          let serial = "";
          if (data.sensorSerial) serial = data.sensorSerial;
          else if (data.moduleSerial) serial = data.moduleSerial;
          else if (data.sandwichSerial) serial = data.sandwichSerial;
          else if (data.fehSerial) serial = data.fehSerial;
          else if (data.sehSerial) serial = data.sehSerial;
          else if (data.rohSerial) serial = data.rohSerial;
          else if (data.vtrxSerial) serial = data.vtrxSerial;
          await uploadBytes(imageRef, image, {
            customMetadata: {
              metadata: JSON.stringify({
                path: filePath,
                module: serial,
                title: `${stepName} image`,
                comments: `Image from ` + stepName,
                userId: user?.firebaseUser?.uid || "",
                timestamp: Timestamp.now(),
                userName:
                  user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : "",
                ...data.metadata,
              }),
            },
          });
        }
      }

      submit(transformer(data));
    },

    [submit, transformer, initialModuleId],
  );

  return (
    <JsonForm
      fields={newFormControl}
      onSubmit={submitWithTransformer}
      onError={console.log}
      disabled={disabled}
      displayClear
      clearAfterSubmit
    />
  );
}

export { FormRenderer };
